// The Fleetly Academy: everyone who came in without a valid PrDP, where each
// person now stands, who Fleetly is sponsoring for the track, and the money
// requests raised for them.
//
// Every stage and every figure comes from Postgres (academy_board_v1 and
// academy_requests_list_v1, db/migrations/0367 and 0367b). The page arranges
// them. CLAUDE.md rule 4.
//
// THE ORDER OF A REQUEST IS THE POINT, as it is for Driver Loans: raised by
// Musa, signed by the member, then paid by Luyanda, who is emailed the moment
// they sign. The pay button exists only for him and only after the signature.
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft, BadgeCheck, ExternalLink, GraduationCap, HandCoins, Mail, MapPin, Phone, RefreshCw, Send, Star,
} from 'lucide-react'
import {
  useCancelAcademyRequest, useCheckAcademySignatures, useGetAcademyBoard, useGetAcademyProofUrl,
  useGetAcademyRequestDocumentLink, useGetAcademyRequestOptions, useGetAcademyRequests, useMarkAcademyRequestPaid,
  useMoveSponsorshipToLoans, useResendAcademyRequest, useSetAcademySponsored,
} from '../hooks/backend/academy'
import { Chip } from '../components/interviews/Chip'
import { Button } from '../lib/shadcn/button'
import { Input } from '../lib/shadcn/input'
import { Spinner } from '../lib/shadcn/spinner'
import { cn } from '../lib/shadcn/utils'
import { prdpLine } from '../utils/screening'
import { formatWhen, formatZar } from '../utils/loans'
import { RaiseAcademyRequestDialog } from '../components/academy/RaiseAcademyRequestDialog'
import {
  REQUEST_STATUS, matchesSearch, matchesView,
  type AcademyBoard, type AcademyFlags, type AcademyMember, type AcademyRequest, type AcademyView, type RequestOptions,
} from '../utils/academy'

function day(value: string | null | undefined): string {
  if (!value) return 'Not recorded'
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value))
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(value)
}

// Where the application lives in the pipeline, so the link opens the right tab.
function applicationRoute(m: AcademyMember): string | null {
  if (!m.latest_application_id) return null
  const s = m.latest_application_status ?? ''
  if (['active', 'compliance', 'suspended', 'dismissed', 'resigned'].includes(s)) return `/active?app=${m.latest_application_id}`
  if (['rejected_score', 'interview_1_failed', 'interview_2_failed', 'interview_1_no_show', 'withdrawn'].includes(s))
    return `/pipeline?tab=rejected&app=${m.latest_application_id}`
  if (s.startsWith('interview') || s === 'submitted') return `/pipeline?tab=interview&app=${m.latest_application_id}`
  return `/pipeline?tab=offer&app=${m.latest_application_id}`
}

function SponsoredTag() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--brand-soft))] px-2 py-0.5 text-[11px] font-semibold text-[hsl(var(--brand-soft-foreground))]">
      <Star className="h-3 w-3" /> Sponsored
    </span>
  )
}

export default function Academy() {
  const [params, setParams] = useSearchParams()
  const board = useGetAcademyBoard<AcademyBoard>()
  const optionsQuery = useGetAcademyRequestOptions<RequestOptions>()
  const check = useCheckAcademySignatures<{ checked: number; signed: number }>()
  const loadBoard = board.trigger
  const loadOptions = optionsQuery.trigger
  const [search, setSearch] = useState(params.get('q') ?? '')
  const [view, setView] = useState<AcademyView>('all')
  const [flags, setFlags] = useState<AcademyFlags>({ sponsored: false, toPay: false, outsideGauteng: false })
  const selectedId = params.get('person')

  const refresh = useCallback(() => { void loadBoard({}, { skipCache: true }).catch(() => undefined) }, [loadBoard])
  useEffect(() => { refresh() }, [refresh])
  useEffect(() => { void loadOptions({}, { skipCache: true }).catch(() => undefined) }, [loadOptions])

  const data = board.data ?? null
  const all = useMemo(() => data?.members ?? [], [data])
  const rows = useMemo(() => all.filter((m) => matchesView(m, view, flags) && matchesSearch(m, search)), [all, view, flags, search])
  const selected = selectedId ? all.find((m) => m.person_id === selectedId) ?? null : null

  function select(personId: string | null) {
    const next = new URLSearchParams(params)
    if (personId) next.set('person', personId)
    else next.delete('person')
    setParams(next, { replace: true })
  }

  async function checkSignatures() {
    try {
      const r = await check.trigger({})
      if (r && r.signed > 0) refresh()
    } catch {
      // The button's own state shows nothing happened; the next open asks again.
    }
  }

  const toggle = (k: keyof AcademyFlags) => setFlags((f) => ({ ...f, [k]: !f[k] }))

  return (
    <div className="flex h-full min-h-0 flex-col lg:grid lg:grid-cols-[420px_1fr]">
      <aside className={cn('min-h-0 flex-1 flex-col border-b border-border bg-card lg:flex lg:flex-none lg:border-b-0 lg:border-r', selected ? 'hidden lg:flex' : 'flex')}>
        <div className="space-y-2 border-b border-border p-3">
          <div className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-[hsl(var(--brand))]" />
            <h1 className="text-sm font-semibold text-foreground">Fleetly Academy</h1>
            <span className="ml-auto text-xs tabular-nums text-muted-foreground">{all.length} members</span>
            <Button variant="ghost" size="icon" className="h-7 w-7" title="Check signatures" aria-label="Check signatures"
              onClick={() => void checkSignatures()} disabled={check.loading}>
              {check.loading ? <Spinner className="h-3.5 w-3.5" /> : <RefreshCw className="h-3.5 w-3.5" />}
            </Button>
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, phone or town"
            className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-[hsl(var(--brand))]"
          />
          {/* The pipeline, in order. Each tab is one stage from academy_stage_v2. */}
          <div className="flex flex-wrap gap-1" role="tablist" aria-label="Academy stage">
            <StageTab active={view === 'all'} onClick={() => setView('all')} label="All" count={all.length} />
            {(data?.stages ?? []).map((s) => (
              <StageTab key={s.key} active={view === s.key} onClick={() => setView(s.key)} label={s.label} count={s.count} muted={s.count === 0} />
            ))}
          </div>
          <div className="flex flex-wrap gap-1">
            <FlagChip on={flags.sponsored} onClick={() => toggle('sponsored')} label={`Sponsored ${data?.sponsored_count ?? 0}`} />
            {(data?.to_pay_count ?? 0) > 0 && <FlagChip on={flags.toPay} onClick={() => toggle('toPay')} label={`Ready to pay ${data?.to_pay_count}`} strong />}
            <FlagChip on={flags.outsideGauteng} onClick={() => toggle('outsideGauteng')} label="Outside Gauteng" />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {board.loading && !data ? (
            <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground"><Spinner /> Loading the Academy</div>
          ) : board.error ? (
            <p className="p-4 text-sm text-rose-700">{board.error}</p>
          ) : rows.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">Nobody matches.</p>
          ) : (
            <ul>
              {rows.map((m) => (
                <li key={m.intake_id}>
                  <button type="button" onClick={() => select(m.person_id)}
                    className={cn('w-full border-b border-border px-4 py-2.5 text-left transition-colors hover:bg-muted/50', selectedId === m.person_id ? 'bg-muted' : '')}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium text-foreground">{m.full_name || 'Unnamed'}</span>
                      <span className="shrink-0 text-[11px] text-muted-foreground">{day(m.enrolled_at)}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <Chip tone={m.stage_tone}>{m.stage_label}</Chip>
                      {m.sponsored && <SponsoredTag />}
                      {m.to_pay > 0 && <Chip tone="brand">ready to pay</Chip>}
                      {m.open_requests > 0 && <Chip tone="amber">request waiting to be signed</Chip>}
                      {m.province && <span className="text-[11px] text-muted-foreground">{m.province}</span>}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>

      <main className={cn('min-h-0 flex-1 flex-col overflow-y-auto bg-background lg:flex', selected ? 'flex' : 'hidden lg:flex')}>
        {selected && data ? (
          <AcademyDetail key={selected.intake_id} m={selected} board={data} options={optionsQuery.data ?? null}
            onBack={() => select(null)} onChanged={refresh} />
        ) : (
          <div className="p-6 text-sm text-muted-foreground">
            {selectedId && !board.loading ? 'That person is not in the Academy list.' : 'Choose someone to see where they stand.'}
          </div>
        )}
      </main>
    </div>
  )
}

function StageTab({ active, onClick, label, count, muted }: { active: boolean; onClick: () => void; label: string; count: number; muted?: boolean }) {
  return (
    <button type="button" role="tab" aria-selected={active} onClick={onClick}
      className={cn('rounded-sm px-2.5 py-1 text-xs font-medium transition-colors',
        active ? 'bg-foreground text-background' : muted ? 'bg-muted/50 text-muted-foreground/60 hover:text-foreground' : 'bg-muted text-muted-foreground hover:text-foreground')}>
      {label} <span className="tabular-nums">{count}</span>
    </button>
  )
}

function FlagChip({ on, onClick, label, strong }: { on: boolean; onClick: () => void; label: string; strong?: boolean }) {
  return (
    <button type="button" aria-pressed={on} onClick={onClick}
      className={cn('rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors',
        on ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
          : strong ? 'border-[hsl(var(--brand))] text-[hsl(var(--brand))]' : 'border-border text-muted-foreground hover:text-foreground')}>
      {label}
    </button>
  )
}

function AcademyDetail({ m, board, options, onBack, onChanged }: {
  m: AcademyMember; board: AcademyBoard; options: RequestOptions | null; onBack: () => void; onChanged: () => void
}) {
  const route = applicationRoute(m)
  const prdp = m.latest_application_id ? prdpLine(m) : null
  const requestsQuery = useGetAcademyRequests<AcademyRequest[]>()
  const loadRequests = requestsQuery.trigger
  const sponsor = useSetAcademySponsored()
  const move = useMoveSponsorshipToLoans<{ loan_ref: string; amount_zar: number; instalments: number }>()
  const [raising, setRaising] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const refreshRequests = useCallback(() => {
    void loadRequests({ intake_id: m.intake_id }, { skipCache: true }).catch(() => undefined)
  }, [loadRequests, m.intake_id])
  useEffect(() => { refreshRequests() }, [refreshRequests])
  const requests = requestsQuery.data ?? []

  const changed = () => { refreshRequests(); onChanged() }

  async function setSponsored(on: boolean) {
    setError(null)
    try { await sponsor.trigger({ intake_id: m.intake_id, sponsored: on }); onChanged() }
    catch (e) { setError(e instanceof Error ? e.message : String(e)) }
  }

  async function moveToLoans() {
    setError(null); setNotice(null)
    try {
      const r = await move.trigger({ intake_id: m.intake_id })
      if (r) setNotice(`${formatZar(r.amount_zar)} is now ${r.loan_ref} on Driver Loans, recovered over ${r.instalments} payslips.`)
      changed()
    } catch (e) { setError(e instanceof Error ? e.message : String(e)) }
  }

  return (
    <div className="space-y-5 p-4 lg:p-6">
      <button type="button" onClick={onBack} className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground lg:hidden">
        <ArrowLeft className="h-4 w-4" /> Back to the Academy
      </button>

      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold text-foreground">{m.full_name || 'Unnamed'}</h2>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            <Chip tone={m.stage_tone}>{m.stage_label}</Chip>
            {m.sponsored && <SponsoredTag />}
            {m.source_label && <Chip tone="neutral">{m.source_label}</Chip>}
          </div>
        </div>
        {board.can_request && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" disabled={sponsor.loading} onClick={() => void setSponsored(!m.sponsored)}>
              <Star className="mr-1 h-3.5 w-3.5" /> {m.sponsored ? 'Remove sponsored tag' : 'Tag as sponsored'}
            </Button>
            <Button variant="brand" size="sm" onClick={() => setRaising(true)} disabled={!options}>
              <HandCoins className="mr-1 h-3.5 w-3.5" /> Raise a request
            </Button>
          </div>
        )}
      </div>

      {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">{error}</p>}
      {notice && <p className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100">{notice}</p>}

      <section className="grid gap-2 text-sm sm:grid-cols-2">
        <span className="inline-flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" />{m.primary_email || 'No email on file'}</span>
        <span className="inline-flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" />{m.primary_phone || m.whatsapp_phone || 'No phone on file'}</span>
        <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-muted-foreground" />{[m.town, m.province].filter(Boolean).join(', ') || 'No area on file'}</span>
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold text-foreground">Where they are</h3>
        <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <Field k="Stage" v={m.stage_label} />
          <Field k="Joined the Academy" v={day(m.enrolled_at)} />
          <Field k="Invited to apply" v={m.apply_invite_sent_at ? day(m.apply_invite_sent_at) : 'Not yet'} />
          <Field k="Latest application" v={m.latest_application_id ? day(m.latest_application_at) : 'None'} />
          <Field k="Uber profile" v={m.has_uber_profile ? 'Yes' : 'No'} />
          {m.dropped_out_at && <Field k="Left the Academy" v={`${day(m.dropped_out_at)}${m.dropped_out_reason ? `: ${m.dropped_out_reason}` : ''}`} wide />}
          {m.notes && <Field k="Notes" v={m.notes} wide />}
        </dl>
        {prdp && <div className="mt-2"><Chip tone={prdp.tone}>{prdp.label}</Chip></div>}
        {route && (
          <Link to={route} className="mt-3 inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-muted">
            <ExternalLink className="h-4 w-4" /> Open the driver application
          </Link>
        )}
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <h3 className="text-sm font-semibold text-foreground">Sponsorship</h3>
          <span className="text-xs text-muted-foreground">
            {m.sponsored
              ? `Sponsored since ${formatWhen(m.sponsored_at)}${m.sponsored_by_name ? `, by ${m.sponsored_by_name}` : ''}. Requested ${formatZar(m.requested_zar)}, paid ${formatZar(m.paid_zar)}.`
              : 'Not sponsored. Raising a request tags them sponsored.'}
          </span>
        </div>

        {m.recoverable_zar > 0 && (
          m.stage === 'driver' ? (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] p-3 text-xs text-[hsl(var(--brand-soft-foreground))]">
              <BadgeCheck className="h-4 w-4" />
              <span className="flex-1">They now drive for Fleetly, so the {formatZar(m.recoverable_zar)} paid for them is recovered as they agreed, over {options?.recovery_instalments ?? 3} payslips.</span>
              {board.can_recover && (
                <Button size="sm" variant="brand" disabled={move.loading} onClick={() => void moveToLoans()}>Move to Driver Loans</Button>
              )}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">{formatZar(m.recoverable_zar)} paid for them is recovered only if they become a Fleetly driver. Nothing is owed until then.</p>
          )
        )}

        {requestsQuery.loading && requests.length === 0 ? (
          <p className="flex items-center gap-2 text-xs text-muted-foreground"><Spinner className="h-3 w-3" /> Loading requests</p>
        ) : requestsQuery.error ? (
          <p className="text-xs text-rose-700">{requestsQuery.error}</p>
        ) : requests.length === 0 ? (
          <p className="text-xs text-muted-foreground">No money has been requested for them.</p>
        ) : (
          <ul className="space-y-2">
            {requests.map((r) => <RequestCard key={r.id} r={r} canRelease={board.can_release} canAct={board.can_request} onChanged={changed} />)}
          </ul>
        )}
      </section>

      {raising && (
        <RaiseAcademyRequestDialog open={raising} onOpenChange={setRaising} member={m} options={options} onSaved={changed} />
      )}
    </div>
  )
}

function Field({ k, v, wide }: { k: string; v: string; wide?: boolean }) {
  return (
    <div className={wide ? 'sm:col-span-2' : undefined}>
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{k}</dt>
      <dd className="whitespace-pre-line">{v}</dd>
    </div>
  )
}

function RequestCard({ r, canRelease, canAct, onChanged }: { r: AcademyRequest; canRelease: boolean; canAct: boolean; onChanged: () => void }) {
  const resend = useResendAcademyRequest()
  const cancel = useCancelAcademyRequest()
  const paid = useMarkAcademyRequestPaid()
  const link = useGetAcademyRequestDocumentLink<{ url: string }>()
  const proof = useGetAcademyProofUrl<{ url: string }>()
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [cancelling, setCancelling] = useState(false)
  const [reason, setReason] = useState('')
  const st = REQUEST_STATUS[r.status]
  const readyToPay = r.status === 'signed' && !r.paid_out_at

  const run = async (fn: () => Promise<unknown>) => {
    setError(null)
    try { await fn(); onChanged() } catch (e) { setError(e instanceof Error ? e.message : String(e)) }
  }
  // Opened before the await so the browser treats it as the click's own window.
  const open = async (get: () => Promise<{ url: string } | null | undefined>) => {
    setError(null)
    const w = window.open('', '_blank')
    try { const x = await get(); if (x?.url && w) w.location.href = x.url; else w?.close() }
    catch (e) { w?.close(); setError(e instanceof Error ? e.message : String(e)) }
  }

  return (
    <li className="space-y-2 rounded-md border border-border p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs text-muted-foreground">{r.ref}</span>
        <span className="font-semibold tabular-nums">{formatZar(r.total_zar)}</span>
        <Chip tone={st.tone}>{st.label}</Chip>
        {readyToPay && <Chip tone="brand">ready to pay</Chip>}
        {r.paid_out_at && <Chip tone="green">paid</Chip>}
        {r.converted_loan_ref && <Chip tone="neutral">recovering as {r.converted_loan_ref}</Chip>}
      </div>
      <ul className="text-xs text-foreground">
        {r.items.map((i, n) => (
          <li key={n} className="flex justify-between gap-4">
            <span>{i.label}{i.detail ? `: ${i.detail}` : ''}</span>
            <span className="tabular-nums">{formatZar(i.amount_zar)}</span>
          </li>
        ))}
      </ul>
      <dl className="grid gap-x-6 gap-y-0.5 text-xs text-muted-foreground sm:grid-cols-2">
        <div>Paid into: <span className="text-foreground">{r.payout_text}</span></div>
        <div>Raised by {r.requested_by_name}, {formatWhen(r.requested_at)}</div>
        {r.sent_at && <div>Sent to sign {formatWhen(r.sent_at)}{r.signer_email ? `, to ${r.signer_email}` : ''}</div>}
        {r.signed_at && <div>Signed {formatWhen(r.signed_at)}</div>}
        {r.notified_at && <div>Luyanda emailed {formatWhen(r.notified_at)}</div>}
        {r.paid_out_at && <div>Paid {formatWhen(r.paid_out_at)} by {r.paid_out_by_name}, {r.paid_out_method === 'emailed_proof' ? 'proof of payment emailed' : 'marked paid'}</div>}
        {r.declined_at && <div>Declined {formatWhen(r.declined_at)}</div>}
        {r.cancelled_at && <div>Cancelled {formatWhen(r.cancelled_at)}: {r.cancel_reason}</div>}
        {r.note && <div className="sm:col-span-2">Note: {r.note}</div>}
      </dl>
      {r.status === 'send_failed' && r.send_error && (
        <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200">{r.send_error}</p>
      )}

      {readyToPay && canRelease && (
        <div className="space-y-2 rounded-md border border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] p-3 text-xs text-[hsl(var(--brand-soft-foreground))]">
          <p className="text-sm font-semibold">Pay {formatZar(r.total_zar)} by {r.payout_text}.</p>
          <p>When it is paid, press I have paid, or email the proof of payment to admin@fleetly.co.za with {r.ref} in the subject. Replying to the email you were sent does exactly that.</p>
          <span className="flex flex-wrap items-center gap-1">
            <Input className="h-8 w-60 bg-background text-xs" placeholder="Optional note, e.g. eWallet from FNB app" value={note} onChange={(e) => setNote(e.target.value)} />
            <Button size="sm" variant="brand" disabled={paid.loading} onClick={() => void run(() => paid.trigger({ id: r.id, note }))}>I have paid</Button>
          </span>
        </div>
      )}
      {readyToPay && !canRelease && (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-100">Signed. Waiting for Luyanda to pay{r.notified_at ? `, who was emailed ${formatWhen(r.notified_at)}` : ''}.</p>
      )}

      {r.proofs.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="font-medium">Proof of payment:</span>
          {r.proofs.map((p) => (
            <Button key={p.id} size="sm" variant="outline" className="h-7" disabled={proof.loading}
              onClick={() => void open(() => proof.trigger({ id: p.id }, { skipCache: true }))}>
              <ExternalLink className="mr-1 h-3.5 w-3.5" /> {p.filename ?? 'Attachment'}
            </Button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {r.has_document && (
          <Button size="sm" variant="outline" disabled={link.loading} onClick={() => void open(() => link.trigger({ id: r.id }, { skipCache: true }))}>
            <ExternalLink className="mr-1 h-3.5 w-3.5" /> {r.status === 'signed' ? 'Open the signed page' : 'Open the page they were sent'}
          </Button>
        )}
        {canAct && r.status === 'send_failed' && (
          <Button size="sm" variant="brand" disabled={resend.loading} onClick={() => void run(() => resend.trigger({ id: r.id }))}>
            <Send className="mr-1 h-3.5 w-3.5" /> Send again
          </Button>
        )}
        {canAct && ['sending', 'send_failed', 'awaiting_signature'].includes(r.status) && !cancelling && (
          <Button size="sm" variant="ghost" onClick={() => setCancelling(true)}>Cancel this request</Button>
        )}
        {cancelling && (
          <span className="flex flex-wrap items-center gap-1">
            <Input className="h-8 w-60 text-xs" placeholder="Why it is being cancelled" value={reason} onChange={(e) => setReason(e.target.value)} />
            <Button size="sm" variant="destructive" disabled={cancel.loading}
              onClick={() => void run(async () => { await cancel.trigger({ id: r.id, reason }); setCancelling(false) })}>Cancel it</Button>
            <Button size="sm" variant="ghost" onClick={() => setCancelling(false)}>Keep it</Button>
          </span>
        )}
      </div>
      {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-950 dark:text-rose-200">{error}</p>}
    </li>
  )
}
